import { createReducer, on } from '@ngrx/store';
import { FormatPercentPipe } from '../../shared/pipes/FormatPercentPipe';
import { amountTypeDict, StwProcessCode, transactionTypes } from '../../shared/utilities/Constants';
import { ViewPageActions } from '../../transaction-view/actions';
import {
  AchActions,
  ConfirmationPageActions,
  ReviewPageActions, StandingAuthorizationsActions, TransactionDetailsActions, TransactionFormActions
} from '../actions';
import { AchInfo } from '../models/ach/AchInfo';
import { AmountType } from '../../shared/types/Transaction';
import { InitReleaseActions } from '../../initiate-and-release/actions';
import { TaxAmountAndPercentageFormatter } from '../../shared/utilities/TaxAmountAndPercentageFormatter';

export const achInfoKey = 'achInfo';

export type State = AchInfo;

export const initialAchInfoState: State = {
  nickName: '',
  withholdFedTax: false,
  withholdStateTax: false,
  fedTaxPercent: '',
  fedTaxAmount: '',
  stateTaxPercent: '',
  stateTaxAmount: '',
  canDisplayStateTax: false,
  distributionReason: '',
  distributionReasonMap: [],
  distributionReasonsList: [],
  stateCd: '',
  stateTypeIndicator: '',
  stateTaxAttentionRequiredMI: false,
  stateTaxAttentionRequiredOther: false,
  stwProcessCode: '',
  maxTransferAmount: '',
  toFromAccountNumber: '',
  amountType: amountTypeDict.gross,
  transferAmount: '',
  withdrawalAmount: '',
  profileAmount: '',
  contributionOptions: [],
  contributionReason: '',
  contributionCode: '',
  contributionSubCode: '',
  standingAuthorizationsCallSucceeded: false,
  inputCallFinished: false,
  verifyCallFinished: false,
  inquiryCallFinished: false,
  acknowledgeCallFinished: false,
  grossNetUpdated: false,
  distributionReasonUpdated: false,
  availableCashMMF: '',
  availableCashMargin: '',
  pendReasons: [],
  acknowledgementArtifactContent: '',
  toFromAccount: {
    abaNumber: '',
    accountName: '',
    accountNumber: '',
    accountType: '',
    financialInstitution: ''
  },
  endDate: '',
  messageFields: {
    errorList: [],
    warningList: [],
    hasError: false,
    hasWarning: false
  },
  attentionMessageList: [],
  moneyLinkType: '',
  frequencyList: [],
  frequency: '',
  txnCode: '',
  fiAccountType: '',
  cashieringDates: { initiationDate: '' },
  cashieringIraAgreementText: '',
  isCreateAchProfile: false,
  processDate: '',
  secondProcessDate: '',
  defaultProcessDate: '',
  cashieringDateCallFinished: false,
  defaultFedTaxPercent: '',
  defaultStateTaxPercent: '',
  defaultAmountType: '',
  defaultDistributionReason: '',
  cashieringRequestId: '',
  authResponse: {
    draftId: '',
    pendForRelease: false,
    pendForReleaseReason: '',
    canRelease: false
  },
  thirdPartyInd: '',
  revalidation: {
    status: '',
    errors: [],
    fieldsUpdated: []
  },
  refId: '',
  confirmationId: '',
  transferId: ''
};

export const achReducer = createReducer(
  initialAchInfoState,
  on(StandingAuthorizationsActions.loadSloas, (state) => {
    return {
      ...state,
      standingAuthorizationsCallSucceeded: false,
      inputCallFinished: false
    };
  }),
  on(
    StandingAuthorizationsActions.loadSloasSuccess,
    (state) => {
      return {
        ...state,
        standingAuthorizationsCallSucceeded: true,
      };
    }
  ),
  on(
    StandingAuthorizationsActions.loadSloasFailure,
    (state) => {
      return {
        ...state,
        standingAuthorizationsCallSucceeded: false,
      };
    }
  ),
  on(AchActions.callAchInput, (state) => {
    return {
      ...state,
      serviceError: resetServiceError(),
      inputCallFinished: false,
      verifyCallFinished: false,
    };
  }),
  on(AchActions.resetTaxWithholdingsToDefault, (state) => {
    return {
      ...state,
      fedTaxPercent: state.defaultFedTaxPercent,
      stateTaxPercent: state.defaultStateTaxPercent,
      amountType: state.defaultAmountType,
      distributionReason: state.defaultDistributionReason
    };
  }),
  on(TransactionFormActions.callAchAddProfileVerify, (state) => {
    return {
      ...state,
      isCreateAchProfile: true
    };
  }),
  on(TransactionFormActions.callAchAddProfileVerify, (state, request) => {
    return {
      ...state,
      // get gross/net amount type & distribution reason from request
      amountType: request.transactionForm.transactionDetails?.amountType as AmountType,
      distributionReason: request.transactionForm.transactionDetails?.taxWithholdingsFormGroup?.distributionReason ?? ''
    };
  }),
  on(
    TransactionFormActions.callAchVerify,
    TransactionFormActions.callAchAddProfileVerify,
    ReviewPageActions.editClicked,
    (state) => {
      return {
        ...state,
        verifyCallFinished: false,
      };
    }
  ),
  on(TransactionFormActions.editLoadComplete, (state) => {
    return {
      ...state,
      inputCallFinished: true,
      standingAuthorizationsCallSucceeded: true,
    };
  }),
  on(
    AchActions.inputCallSuccessful,
    AchActions.verifyCallSuccessful,
    AchActions.acknowledgeCallSuccessful,
    (state) => {
      return {
        ...state,
        serviceError: resetServiceError(),
      };
    }
  ),
  on(TransactionFormActions.distributionReasonChanged, (state, action) => {
    if (action.transactionType === transactionTypes.ach) {
      return {
        ...state,
        distributionReason: action.distributionReason.description
      };
    }
    return { ...state };
  }),
  on(AchActions.inputCallSuccessful, (state, response) => {
    return {
      ...state,
      withholdFedTax: response.achInfo.withholdFedTax,
      withholdStateTax: response.achInfo.withholdStateTax,
      fedTaxPercent: response.achInfo.fedTaxPercent,
      stateTaxPercent: response.achInfo.stateTaxPercent,
      canDisplayStateTax: response.achInfo.iraFields.canDisplayStateTax,
      distributionReason: response.achInfo.distributionReason,
      distributionReasonsList: response.achInfo.distributionReasonsList,
      stateCd: response.achInfo.stateCd,
      stateTypeIndicator: response.achInfo.iraFields?.stateTypeInd,
      stateTaxAttentionRequiredMI: response.achInfo.stateTaxAttentionRequiredMI,
      stateTaxAttentionRequiredOther:
        response.achInfo.stateTaxAttentionRequiredOther,
      contributionOptions: response.achInfo.contributionOptions,
      contributionCode: response.achInfo.contributionCode,
      contributionSubCode: response.achInfo.contributionSubCode,
      maxTransferAmount: response.achInfo.maxTransferAmount,
      toFromAccountNumber: response.achInfo.toFromAccountNumber,
      amountType: response.achInfo.amountType,
      inputCallFinished: true,
      availableCashMargin: response.achInfo.availableCashMargin,
      availableCashMMF: response.achInfo.availableCashMMF,
      toFromAccount: response.achInfo.toFromAccount,
      fiAccountType: response.achInfo.fiAccountTypeCode,
      frequencyList: response.achInfo.frequencyList,
      cashieringIraAgreementText: response.achInfo.cashieringIraAgreementText,
      processDate: response.achInfo.processDate,
      stwProcessCode: response.achInfo.iraFields.stwProcessCode as StwProcessCode,
      defaultProcessDate: response.achInfo.processDate,
      defaultFedTaxPercent: response.achInfo.fedTaxPercent,
      defaultStateTaxPercent: response.achInfo.stateTaxPercent,
      defaultAmountType: response.achInfo.amountType,
      defaultDistributionReason: response.achInfo.distributionReason
    };
  }),
  on(AchActions.callInquiry, (state, { payload }) => {
    return {
      ...state,
      txnCode: payload.txnCode
    };
  }),
  on(AchActions.inquiryCallSuccessful, (state, { achInquiryResponse }) => {
    const percentFormatPipe = new FormatPercentPipe();
    return {
      ...state,
      nickName: achInquiryResponse.nickName,
      fedTaxAmount: `$${achInquiryResponse.iraFields.fedTaxAmount}`,
      stateTaxAmount: `$${achInquiryResponse.iraFields.stateTaxAmount}`,
      transferAmount: achInquiryResponse.netAmt,
      withdrawalAmount: achInquiryResponse.grossAmt,
      pendReasons: achInquiryResponse.pendReasonsList,
      toFromAccount: achInquiryResponse.toFromAccount,
      distributionReason: achInquiryResponse.iraFields.isIraTransaction ? achInquiryResponse.distributionReasonDescription : '',
      fedTaxPercent: achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(achInquiryResponse.iraFields.fedTaxPercentage, 3) : '',
      stateTaxPercent: achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(achInquiryResponse.iraFields.stateTaxPercentage.replace(/[()]/g, ''), 3) : '',
      endDate: achInquiryResponse.endDate,
      maxTransferAmount: achInquiryResponse.maxTransferAmount,
      moneyLinkType: achInquiryResponse.moneyLinkType,
      frequencyList: achInquiryResponse.frequencyList,
      inquiryCallFinished: true,
      withholdStateTax: achInquiryResponse.iraFields.isWithholdStateTax,
      distributionReasonMap: achInquiryResponse.iraFields.distributionReasonMap,
      frequency: achInquiryResponse.frequency,
      contributionReason: achInquiryResponse.contributionReason,
      cashieringRequestId: achInquiryResponse.cashieringReqId,
      cashieringRequestSecondId: achInquiryResponse.cashieringReqSecondaryId,
      secondProcessDate: achInquiryResponse.secondProcessDate,
      amountType: mapAmountType(false, state.amountType, achInquiryResponse.grossNetIndicator)
    };
  }),
  on(AchActions.verifyCallSuccessful, (state, { response }) => {
    const fedTax = TaxAmountAndPercentageFormatter(
      response?.iraFields?.fedTaxFormatted ?? ''
    );
    const stateTax = TaxAmountAndPercentageFormatter(
      response?.iraFields?.stateTaxFormatted ?? ''
    );

    return {
      ...state,
      distributionReasonUpdated: isProfileInformationUpdated(state.distributionReason ?? '', response.distributionReasonDescription, response.isExistingProfile),
      grossNetUpdated: isProfileInformationUpdated(state.amountType === undefined ? '' : state.amountType[0]?.toLowerCase() ?? '', response.iraFields.grossNetIndicator.toLowerCase(), response.isExistingProfile),
      fedTaxAmount: response.iraFields.fedTaxFormatted,
      stateTaxAmount: response.iraFields.stateTaxFormatted,
      messageFields: response.messageFields,
      attentionMessageList: response.attentionMessagesList,
      transferAmount: response.transferAmount ? response.transferAmount : state.transferAmount,
      withdrawalAmount: response.totalWithdrawalAmount,
      pendReasons: response.pendReasonsList,
      moneyLinkType: response.moneyLinkType,
      verifyCallFinished: true,
      acknowledgementArtifactContent: response.acknowledgementArtifactContent,
      processDate: response.processDate,
      fedTaxPercent: fedTax?.percentage ?? '',
      stateTaxPercent: stateTax?.percentage ?? '',
      distributionReason: response.distributionReasonDescription,
      withholdStateTax: response.iraFields.isStateTaxWithheld,
      withholdFedTax: response.iraFields.isFederalTaxWithheld,
      amountType: mapAmountType(response.messageFields?.hasError, state.amountType, response.iraFields.grossNetIndicator),
      cashieringRequestId: response.cashieringRequestId.toString(),
      authResponse: response.moneyLinkAuthResponse,
      toFromAccount: {
        ...state.toFromAccount,
        accountType: response.toFromAccount?.accountType,
        accountName: response.toFromAccount?.accountName || state.toFromAccount.accountName //keep existing account name if response value is empty
      },
      revalidation: {
        ...response.revalidation,
        status: response.revalidation?.status,
        errors: response.revalidation?.errors,
        fieldsUpdated: response.revalidation?.fieldsUpdated
      },
      refId: response.refId ?? '',
      confirmationId: response.confirmationId ?? '',
      transferId: response.transferId ?? ''

    };
  }),
  on(AchActions.acknowledgeCallSuccessful, (state, { response }) => {
    return {
      ...state,
      acknowledgeCallFinished: true,
      messageFields: response.messageFields,
      processDate: response.processDate,
      distributionReason: response.distributionReasonDescription ? response.distributionReasonDescription : state.distributionReason
    };
  }),

  on(
    TransactionFormActions.cancelClicked,
    ReviewPageActions.cancelClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    ConfirmationPageActions.createAnotherRequestClicked,
    ViewPageActions.backButtonClicked,
    InitReleaseActions.getInitReleaseList,
    ViewPageActions.backButtonClickedFromMsv,
    () => {
      return initialAchInfoState;
    }
  ),
  on(TransactionFormActions.transactionTypeChanged, (state) => {
    return {
      ...state,
      verifyCallFinished: false
    };
  }),
  on(ReviewPageActions.clearAttentionMessage, (state) => {
    return {
      ...state,
      messageFields: {
        errorList: [],
        warningList: [],
        hasError: false,
        hasWarning: false
      },
      pendReasons: [],
      attentionMessageList: []
    };
  }),
  on(TransactionDetailsActions.loadWithdrawalDateSuccess, (state, cashieringDates) => {
    return {
      ...state,
      cashieringDates: { initiationDate: cashieringDates.initiationDate, },
      cashieringDateCallFinished: true
    };
  }),
  on(TransactionDetailsActions.loadWithdrawalDate, (state) => {
    return {
      ...state,
      cashieringDateCallFinished: false,
      cashieringDates: initialAchInfoState.cashieringDates,
    };
  }),
  on(TransactionDetailsActions.loadWithdrawalDateFailure, (state) => {
    return {
      ...state,
      cashieringDateCallFinished: true,
      cashieringDates: initialAchInfoState.cashieringDates,
    };
  }),
  on(InitReleaseActions.initReleaseByDraftIdSuccessful, (state, { response, isSloa }) => {
    if (isSloa || response.transactionType !== 'moneylink') {
      return state;
    }

    // Needed for cross-ui compatibility since drafts created from legacy ui doesn't contain grossNetIndicator
    const grossNetIndicator = response.draftObject?.grossNetIndicator
      ? response.draftObject.grossNetIndicator === 'true'
      : response.draftObject?.totalWithdrawalAmount !== response.draftObject?.amount;

    return {
      ...state,
      authResponse: {
        ...state.authResponse,
        draftId: response.draftId
      },
      toFromAccount: {
        financialInstitution: response?.draftObject?.addProfileAttributes ? response?.draftObject?.addProfileAttributes?.financialInstitution ?? '' : response?.draftObject?.financialInstitution ?? response?.draftObject?.FinancialInstitution ?? '',
        abaNumber: response?.draftObject?.abaNumber ?? response?.draftObject?.ABANumber ?? '',
        accountNumber: response?.draftObject?.addProfileAttributes ? response?.draftObject?.addProfileAttributes?.bankAccountId ?? '' : response?.draftObject?.transferAccount ?? response?.draftObject?.TransferAccount ?? '',
        accountName: response?.draftObject?.addProfileAttributes ? response?.draftObject?.addProfileAttributes?.bankAccountTitleName ?? '' : response?.draftObject?.accountName ?? response?.draftObject?.AccountName ?? '',
        accountType: response?.draftObject?.addProfileAttributes ? response?.draftObject?.addProfileAttributes?.bankAccountTypeCode ?? '' : response?.draftObject?.accountType ?? response?.draftObject?.AccountType ?? ''
      },
      toFromAccountNumber: response?.draftObject?.addProfileAttributes ? response?.draftObject?.addProfileAttributes?.bankAccountId ?? '' : response?.draftObject?.transferAccount ?? response?.draftObject?.TransferAccount ?? '',
      distributionReasonMap: [
        {
          code: response.draftObject.distributionReason?.toString()[0] ?? '',
          subCode: '',
          description: response.draftObject.distributionReasonDescription ?? ''
        }
      ],
      fedTaxPercent: state.fedTaxPercent,
      cashieringDates: {
        ...state.cashieringDates,
        initiationDate: response.draftObject.requestedWithdrawalDate ?? ''
      },
      thirdPartyInd: response.draftObject?.thirdPartyIndValue ?? '',
      contributionCode: response?.draftObject?.addProfileAttributes?.contributionCode ?? '',
      contributionSubCode: response?.draftObject?.addProfileAttributes?.contributionSubCode ?? '',
      maxTransferAmount: response?.draftObject?.addProfileAttributes?.maxTransferAmount.toString() ?? '',
      fiAccountType: response?.draftObject?.addProfileAttributes ? response?.draftObject?.addProfileAttributes?.bankAccountTypeCode ?? '' : response?.draftObject?.accountTypeCode ?? response?.draftObject?.AccountTypeCode ?? '',
      frequency: response?.draftObject?.frequency?.toString() ?? '',
      distributionReason: response.draftObject.distributionReasonDescription ?? '',
      defaultAmountType: grossNetIndicator ? amountTypeDict.gross : amountTypeDict.net,
      transferAmount: response.draftObject.amount?.toString() ?? '',
      amountType: grossNetIndicator ? amountTypeDict.gross : amountTypeDict.net,
      withdrawalAmount: response.draftObject.totalWithdrawalAmount ?? ''
    };
  })
);

function resetServiceError() {
  return {
    headers: {
      normalizedNames: {},
      lazyUpdate: false,
    },
    status: 0,
    statusText: '',
    url: '',
    ok: false,
    name: '',
    message: '',
    loading: false,
    error: {
      errorDescription: '',
      error: '',
    },
  };
}

function mapAmountType(hasError: boolean, currentValue: AmountType, newValue: string): AmountType {
  if (hasError || !newValue) {
    return currentValue;
  }
  if (newValue === 'G') {
    return amountTypeDict.gross;
  }
  return newValue === 'N' ? amountTypeDict.net : '';
}

export function isProfileInformationUpdated(valueFromVerifyRequest: string, valueFromVerifyResponse: string, isExistingProfile: boolean): boolean {
  if ((valueFromVerifyRequest !== valueFromVerifyResponse) && isExistingProfile) {
    return true;
  }
  return false;
}
